import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ageText } from '@/shared/model/age';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Dimension, Score, ScoreFactors } from '../api/scoring';
import {
  cautions,
  leftOut,
  percent,
  points,
  pointsShort,
  segments,
  trustNotes,
  verdict,
} from '../model/factors';
import type { TrustNote, Verdict } from '../model/factors';
import { scoreBand } from '../model/presentation';
import { bandTone } from './tone';

/* The top of the Score tab: the number, what to fix first, where the 100 points went, and how far the
 * number can be relied on.
 *
 * **The header never waits for the explanation.** The score itself is already loaded by the time
 * this renders, so its figure, band and age show at once; only the right-hand side waits for the
 * factors, and a failure to read them says that the score is fine and only its explanation is
 * missing — a fault in one request must not read as a fault in the data.
 *
 * **The segments are buttons**, because selecting one narrows the list of checks below to that
 * dimension; each says in words what it holds, earns and loses, so the bar is never the only way to
 * read a figure.
 */

export type Explanation =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly retry: () => void }
  | {
      readonly state: 'ready';
      readonly factors: ScoreFactors;
      readonly selected: Dimension | null;
      readonly onSelect: (dimension: Dimension) => void;
    };

export interface ScoreVerdictCardProps {
  readonly score: Score;
  readonly explanation: Explanation;
  readonly now: Date;
}

const BAND_LABEL = {
  ok: 'scoring.band.healthy',
  warn: 'scoring.band.watch',
  danger: 'scoring.band.atRisk',
} as const;

export function ScoreVerdictCard({
  score,
  explanation,
  now,
}: ScoreVerdictCardProps): ReactElement {
  const { t } = useTranslation();
  const value = score.value;
  const age = ageText(t, score.computed_at, now);

  return (
    <article className="lq-sf__card" aria-label={t('dataset.score.quality')}>
      <div className="lq-sf__verdictTop">
        <div className="lq-sf__figure">
          <span className="lq-sf__label">{t('dataset.score.quality')}</span>
          <span className="lq-sf__value">
            {value === null ? '—' : value.toFixed(1)}
            {value === null ? null : (
              <span className="lq-sf__outOf">{t('dataset.score.outOf')}</span>
            )}
          </span>
          <span className="lq-sf__bandLine">
            <StatusBadge
              tone={bandTone(value)}
              label={
                value === null
                  ? t('dataset.score.notRated')
                  : t(BAND_LABEL[scoreBand(value)])
              }
            />
            <span className="lq-sf__lostLine">
              {value === null
                ? t('dataset.score.nothingMeasured')
                : t('dataset.score.pointsLost', { points: points(100 - value) })}
            </span>
          </span>
          <span className="lq-sf__when">
            {age === null
              ? t('dataset.score.modelMarker', { version: score.score_model_version })
              : t('dataset.score.computed', { age, version: score.score_model_version })}
          </span>
        </div>

        <div className="lq-sf__explain">
          {explanation.state === 'loading' ? (
            <div className="lq-sf__explainLoading" role="status">
              <span className="lq-sf__explainLoadingText">
                {t('dataset.score.factorsLoading')}
              </span>
              <span className="lq-skeleton__bar lq-sf__skeletonLine" aria-hidden="true" />
              <span
                className="lq-skeleton__bar lq-sf__skeletonLine lq-sf__skeletonLine--short"
                aria-hidden="true"
              />
              <span className="lq-skeleton__bar lq-sf__skeletonBar" aria-hidden="true" />
            </div>
          ) : explanation.state === 'error' ? (
            <div className="lq-sf__explainError" role="status">
              <span className="lq-sf__explainErrorText">
                <span className="lq-sf__explainErrorTitle">
                  {t('dataset.score.factorsErrorTitle')}
                </span>
                <span className="lq-sf__explainErrorBody">
                  {t('dataset.score.factorsErrorBody')}
                </span>
              </span>
              <button
                type="button"
                className="lq-btn lq-btn--secondary"
                onClick={explanation.retry}
              >
                {t('dataset.score.retry')}
              </button>
            </div>
          ) : (
            <Explained
              factors={explanation.factors}
              selected={explanation.selected}
              onSelect={explanation.onSelect}
            />
          )}
        </div>
      </div>

      {explanation.state === 'ready' ? <Trust factors={explanation.factors} /> : null}
    </article>
  );
}

function Explained({
  factors,
  selected,
  onSelect,
}: {
  readonly factors: ScoreFactors;
  readonly selected: Dimension | null;
  readonly onSelect: (dimension: Dimension) => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const read = verdict(factors);
  const split = segments(factors);
  const out = leftOut(factors);
  const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' });
  const dimension = (name: Dimension): string => t(`scoring.dimension.${name}`);

  return (
    <>
      <div className="lq-sf__headline">
        <p className="lq-sf__headlineText">{headlineText(t, read)}</p>
        <p className="lq-sf__headlineSub">{subText(t, read)}</p>
      </div>

      {factors.value === null || split.length === 0 ? (
        <p className="lq-sf__nothingBar">{t('dataset.score.split.nothing')}</p>
      ) : (
        <div className="lq-sf__split">
          <div className="lq-sf__splitHead">
            <span>{t('dataset.score.split.caption')}</span>
            <span className="lq-sf__legend">
              <span className="lq-sf__legendItem">
                <span className="lq-sf__key lq-sf__key--earned" aria-hidden="true" />
                {t('dataset.score.split.earned')}
                <strong className="lq-sf__legendFigure">{points(factors.value)}</strong>
              </span>
              <span className="lq-sf__legendItem">
                <span className="lq-sf__key lq-sf__key--lost" aria-hidden="true" />
                {t('dataset.score.split.lost')}
                <strong className="lq-sf__legendFigure lq-sf__legendLost">
                  {points(factors.points_lost ?? 100 - factors.value)}
                </strong>
              </span>
            </span>
          </div>
          <div
            className="lq-sf__segments"
            role="group"
            aria-label={t('dataset.score.split.label')}
          >
            {split.map((segment) => {
              const words = t('dataset.score.split.segment', {
                dimension: dimension(segment.dimension),
                available: points(segment.available),
                value: segment.value.toFixed(1),
                earned: points(segment.earned),
                lost: points(segment.lost),
              });
              return (
                <button
                  key={segment.dimension}
                  type="button"
                  className="lq-sf__segment"
                  aria-pressed={selected === segment.dimension}
                  aria-label={words}
                  title={words}
                  style={
                    {
                      '--sf-grow': String(segment.available),
                      '--sf-kept': `${((segment.earned / segment.available) * 100).toFixed(2)}%`,
                    } as CSSProperties
                  }
                  onClick={() => {
                    onSelect(segment.dimension);
                  }}
                >
                  <span className="lq-sf__segmentKept" />
                </button>
              );
            })}
          </div>
          <div className="lq-sf__segmentLabels" aria-hidden="true">
            {split.map((segment) => (
              <span
                key={segment.dimension}
                className="lq-sf__segmentLabel"
                data-selected={selected === segment.dimension ? 'true' : undefined}
                style={{ '--sf-grow': String(segment.available) } as CSSProperties}
              >
                <span className="lq-sf__segmentName">{dimension(segment.dimension)}</span>
                <span className="lq-sf__segmentNums">
                  {t('dataset.score.split.nums', {
                    earned: pointsShort(segment.earned),
                    available: pointsShort(segment.available),
                  })}
                </span>
              </span>
            ))}
          </div>
          {out.length > 0 ? (
            <p className="lq-sf__leftOut">
              <span className="lq-sf__hatchKey" aria-hidden="true" />
              {t('dataset.score.split.leftOut', {
                list: list.format(
                  out.map((entry) =>
                    t('dataset.score.split.leftOutItem', {
                      dimension: dimension(entry.dimension),
                      share:
                        entry.modelShare === null ? '—' : percent(entry.modelShare, 0),
                      state: entry.state,
                    }),
                  ),
                ),
              })}
            </p>
          ) : null}
        </div>
      )}
    </>
  );
}

function headlineText(t: TFunction, read: Verdict): string {
  const headline = read.headline;
  switch (headline.kind) {
    case 'nothingMeasured':
      return t('dataset.score.headline.nothing');
    case 'allPassed':
      return t('dataset.score.headline.allPassed');
    case 'onlyOne':
      return t('dataset.score.headline.onlyOne', {
        lost: points(headline.lost),
        rule: headline.rule,
      });
    case 'alone':
      return t('dataset.score.headline.alone', {
        rule: headline.rule,
        share: percent(headline.share, 0),
      });
    case 'few':
      return t('dataset.score.headline.few', {
        count: headline.count,
        points: pointsShort(headline.points),
        lost: pointsShort(headline.lost),
        rule: headline.rule,
      });
    case 'many':
      return t('dataset.score.headline.many', {
        count: headline.count,
        lost: pointsShort(headline.lost),
        rule: headline.rule,
        ruleLost: points(headline.ruleLost),
      });
  }
}

function subText(t: TFunction, read: Verdict): string {
  const headline = read.headline;
  if (headline.kind === 'nothingMeasured') {
    return t('dataset.score.sub.nothing', { silent: headline.silentRules ?? 0 });
  }
  if (headline.kind === 'allPassed') {
    return t('dataset.score.sub.allPassed', { count: headline.checks });
  }
  const lift = read.lift;
  if (lift === null) return '';
  const first = t('dataset.score.sub.lift', {
    rule: lift.rule,
    perTable: lift.perTable ? 'yes' : 'no',
    from: points(lift.from),
    to: points(lift.to),
  });
  if (lift.rest === null) return first;
  const rest =
    lift.rest.count === 1
      ? t('dataset.score.sub.restOne', { points: points(lift.rest.points) })
      : t('dataset.score.sub.restMany', {
          count: lift.rest.count,
          points: points(lift.rest.points),
        });
  return `${first} ${rest}`;
}

function Trust({ factors }: { readonly factors: ScoreFactors }): ReactElement {
  const { t, i18n } = useTranslation();
  const notes = trustNotes(factors);
  const limits = cautions(notes);
  const list = new Intl.ListFormat(i18n.language, { type: 'conjunction' });
  const measured = factors.value !== null;

  return (
    <div className="lq-sf__trust">
      <div className="lq-sf__trustHead">
        <StatusBadge
          tone={limits > 0 ? 'warn' : 'ok'}
          label={t('dataset.score.trust.head', { count: limits })}
        />
        <span className="lq-sf__trustLead">{t('dataset.score.trust.lead')}</span>
      </div>
      <ul className="lq-sf__notes">
        {notes.map((note) => {
          const copy = noteCopy(t, note, {
            measured,
            names: list.format(
              note.dimensions.map((name) => t(`scoring.dimension.${name}`)),
            ),
          });
          return (
            <li key={note.kind} className="lq-sf__note" data-direction={note.direction}>
              <span className="lq-sf__noteDot" aria-hidden="true" />
              <span className="lq-sf__noteText">
                <span className="lq-sf__noteTitle">{copy.title}</span>
                <span className="lq-sf__noteBody">{copy.body}</span>
                {copy.ratio === null ? null : (
                  <span className="lq-sf__noteBar">
                    <span
                      className="lq-sf__noteTrack"
                      role="img"
                      aria-label={t('dataset.score.trust.coverageBar', {
                        share: percent(copy.ratio),
                      })}
                      style={
                        {
                          '--sf-ratio': `${(copy.ratio * 100).toFixed(1)}%`,
                        } as CSSProperties
                      }
                    >
                      <span className="lq-sf__noteFill" />
                    </span>
                    <span className="lq-sf__noteShare" aria-hidden="true">
                      {percent(copy.ratio)}
                    </span>
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface NoteCopy {
  readonly title: string;
  readonly body: string;
  /** A coverage note draws its ratio as a bar as well as saying it. */
  readonly ratio: number | null;
}

function noteCopy(
  t: TFunction,
  note: TrustNote,
  { measured, names }: { readonly measured: boolean; readonly names: string },
): NoteCopy {
  const count = note.count ?? 0;
  const share = note.ratio === null ? '—' : percent(note.ratio, 0);
  switch (note.kind) {
    case 'column_coverage': {
      const total = note.total ?? 0;
      return note.direction === 'positive'
        ? {
            title: t('dataset.score.trust.coverageAllTitle'),
            body: t('dataset.score.trust.coverageAllBody', { total }),
            ratio: note.ratio,
          }
        : {
            title: t('dataset.score.trust.coverageTitle', { count, total }),
            body: t('dataset.score.trust.coverageBody', { missing: total - count }),
            ratio: note.ratio,
          };
    }
    case 'evidence_incomplete':
      return {
        title: t('dataset.score.trust.incompleteTitle'),
        body: t('dataset.score.trust.incompleteBody'),
        ratio: null,
      };
    case 'rules_without_evidence':
      return {
        title: t('dataset.score.trust.silentTitle', { count }),
        body: t('dataset.score.trust.silentBody', {
          count,
          measured: measured ? 'yes' : 'no',
        }),
        ratio: null,
      };
    case 'unattributed_rules':
      return {
        title: t('dataset.score.trust.unattributedTitle', { count }),
        body: t('dataset.score.trust.unattributedBody', { count }),
        ratio: null,
      };
    case 'dimensions_without_evidence':
      return {
        title: t('dataset.score.trust.noEvidenceTitle', { names }),
        body: t('dataset.score.trust.noEvidenceBody', {
          count,
          share,
          measured: measured ? 'yes' : 'no',
        }),
        ratio: null,
      };
    case 'dimensions_unmeasurable':
      return {
        title: t('dataset.score.trust.unmeasurableTitle', { names }),
        body: t('dataset.score.trust.unmeasurableBody', { count, share }),
        ratio: null,
      };
    case 'evidence_complete':
      return {
        title: t('dataset.score.trust.completeTitle'),
        body: t('dataset.score.trust.completeBody', { count }),
        ratio: null,
      };
  }
}
